/**
 * Traducciones de la landing pública de franquicias.
 * Este archivo es SUYO — ver la nota de `shop.ts`.
 *
 * ── Cómo se lee esto ─────────────────────────────────────────────────
 * La CLAVE es el texto en español; el valor, su inglés. El español no
 * necesita mapa: cuando falta la traducción, i18next devuelve la clave.
 *
 * ── El orden sigue al de la página ───────────────────────────────────
 * Las cuatro secciones, de arriba abajo, y al final el cromo (navegación
 * lateral). Buscar una cadena es ir a su sección, no leer el mapa entero.
 *
 * ── Los porcentajes NO se traducen ───────────────────────────────────
 * "30%", "55%" y compañía no están aquí a propósito: son cifras, no
 * texto, y se pintan tal cual en los dos idiomas. Lo que sí se traduce es
 * la frase que los rodea, y por eso el total viaja INTERPOLADO
 * ("reparte {{pct}} a su línea") en vez de partido en dos cadenas: en
 * inglés la cifra cae en otra posición dentro de la frase.
 */
export const franchiseEn: Record<string, string> = {
  // ── 1 · Portada ───────────────────────────────────────────────────
  "Franquicia digital": "Digital franchise",
  "Franquicias Digitales": "Digital Franchises",
  "al año · por usuario": "per year · per user",
  "No es una membresía: es tu propiedad.": "It's not a membership: it's your property.",
  "Adquirir mi franquicia": "Get my franchise",
  "Ver los paquetes": "See the packages",

  // ── 2 · Lo que recibes ────────────────────────────────────────────
  "Lo que recibes": "What you get",
  "Tu tienda, tu marca,": "Your store, your brand,",
  "dentro de It's The Best": "inside It's The Best",
  "Una franquicia digital es tu propiedad dentro de la plataforma: tu dirección propia, tus precios, tus clientes y el respaldo de toda la operación de It's The Best.":
    "A digital franchise is your own property inside the platform: your own address, your prices, your customers and the backing of the entire It's The Best operation.",

  "Tu nombre en la plataforma": "Your name on the platform",
  "Tu tienda vive dentro de It's The Best, con tu propia dirección.":
    "Your store lives inside It's The Best, with your own address.",
  "Branding propio de tu equipo": "Your team's own branding",
  "Tu logo y tu color en toda la tienda que administras.":
    "Your logo and your color across the store you manage.",
  "Promociones sobre el authorship": "Promotions above the authorship price",
  "Ajustas precios hacia arriba; nunca por debajo del authorship.":
    "You adjust prices upward; never below the authorship price.",
  "Habilitas o deshabilitas productos": "You enable or disable products",
  "Tú decides qué productos aparecen en tu tienda.":
    "You decide which products show up in your store.",

  // ── 3 · Lo que ganas (14-sep-2026: fuera el bono de franquicia) ───
  "Lo que ganas con tu franquicia": "What you gain with your franchise",
  "Compras más barato y cobras por tu red": "You buy cheaper and earn from your network",
  "siendo socio activo": "as an active member",
  "La franquicia te da tu tienda y el mejor precio. Las comisiones las genera tu red con el plan de compensación: solo las cobra quien hace su recompra del mes.":
    "The franchise gives you your store and the best price. Commissions come from your network under the compensation plan: only those who make their monthly repurchase earn them.",
  "menos en todos los productos y paquetes, sobre el precio de socio": "off every product and package, on top of the member price",
  "de la primera compra de cada persona que invitas (nivel 1)": "of the first purchase of every person you invite (level 1)",
  "de las primeras compras de los niveles 2 a 5 de tu red": "of the first purchases of levels 2 to 5 of your network",
  "al mes con tus compras te mantienen activo y cobrando": "a month from your own purchases keep you active and earning",

  // ── 4 · Adquirir ──────────────────────────────────────────────────
  "Costo de tu franquicia": "Your franchise cost",

  /*
   * El botón de compra y sus tres finales. El de "todavía no podemos
   * cobrar" NO menciona Stripe ni ninguna variable de configuración a
   * propósito: quien lo lee vino a comprar, no a administrar un
   * servidor. Lo que sí hace es ofrecer la salida que hoy sí funciona.
   */
  "Generando tu link de pago…": "Generating your payment link…",
  "Todavía no podemos cobrar en línea. Estamos terminando de conectar la pasarela de pagos; escríbenos y te ayudamos a adquirir tu franquicia.":
    "We can't take payments online just yet. We're finishing the payment gateway setup; write to us and we'll help you get your franchise.",
  "No pudimos generar tu link de pago. Vuelve a intentarlo.":
    "We couldn't generate your payment link. Please try again.",

  // ── Cromo: la navegación lateral ──────────────────────────────────
  "Secciones de la página": "Page sections",
  "Sección {{n}}": "Section {{n}}",
  "Ir a {{seccion}}": "Go to {{seccion}}",

  /* ── Añadido el 1-sep: lo que veía un cliente en español ── */
  "Adquirir franquicia": "Get a franchise",
  "Añade tu primer stock": "Add your first stock",
  "Añade tu primer stock para tu tienda digital": "Add the first stock for your online store",
  "Comprar directo": "Buy directly",
  "Con la Franquicia Digital, {{paquete}} te sale en:": "With the Digital Franchise, {{paquete}} comes to:",
  "Confirmar compra e ir al pago": "Confirm and go to payment",
  "Conviértete en mayorista con tu franquicia It's The Best.": "Become a wholesaler with your It's The Best franchise.",
  "El inventario con el que arranca tu tienda digital.": "The inventory your online store starts with.",
  "Elegir": "Choose",
  "Elegir mi paquete mayorista": "Choose my wholesale package",
  "Ese paquete no tiene precio en esta moneda.": "That package has no price in this currency.",
  "Franquicia Digital": "Digital Franchise",
  "Franquicia Digital (1 año) — incluida en el paquete": "Digital Franchise (1 year) — included in the package",
  "Franquicia incluida": "Franchise included",
  "Incluida": "Included",
  "No se pudieron cargar los paquetes.": "We couldn't load the packages.",
  "Paquetes mayoristas": "Wholesale packages",
  "Quiero iniciar": "I want to start",
  "Solo este paquete, sin franquicia. Eliges cómo pagar en el siguiente paso.": "Just this package, no franchise. You'll choose how to pay in the next step.",
  "Teléfono para contactarte": "Phone where we can reach you",
  "Todavía no hay paquetes mayoristas publicados.": "No wholesale packages published yet.",
  "Total con franquicia": "Total with franchise",
  "Tu franquicia arranca con inventario: elige un paquete mayorista. Su costo se suma al de la franquicia en un solo pago.": "Your franchise starts with inventory: choose a wholesale package. Its cost is added to the franchise in a single payment.",
  "Tu negocio digital de wellness, con vigencia de un año.": "Your online wellness business, valid for one year.",
  "Tu paquete": "Your package",
  "Volver a Franquicias Digitales": "Back to Digital Franchises",
  "ahorras {{monto}}": "you save {{monto}}",
  "paquete": "package",
  "por año": "per year",
  "{{pv}} PV · {{off}}% de descuento": "{{pv}} PV · {{off}}% off",
  "{{units}} unidades · {{pv}} PV · {{off}}% de descuento": "{{units}} units · {{pv}} PV · {{off}}% off",
  "¿Buscas un paquete de mayor volumen?": "Looking for a larger package?",
};
